import type { CaptureOptions } from "posthog-js";
import { posthogCapture } from "./posthog";
import { pushToDataLayer } from "./gtm";

/**
 * Public analytics API. Owns the event registry and fans every capture out
 * across both sinks — PostHog for product analytics and session replay, GTM's
 * `dataLayer` for GA4 and Google Ads. Capture through here rather than
 * reaching into `./posthog` or `./gtm`, so no sink is silently skipped.
 * (`__root.tsx` imports `./gtm` directly to bootstrap the container and to
 * push `spa_pageview`, which is deliberately GTM-only.)
 */

export { loadPostHog } from "./posthog";

export const EVENTS = {
  // Video engagement (§3.3)
  VSL_IMPRESSION: "vsl_impression",
  VSL_AUTOPLAY: "vsl_autoplay",
  VSL_UNMUTE: "vsl_unmute",
  VSL_MUTE: "vsl_mute",
  VSL_PLAY: "vsl_play",
  VSL_PROGRESS: "vsl_progress",
  VSL_PAUSE: "vsl_pause",
  VSL_COMPLETE: "vsl_complete",
  VSL_REPLAY: "vsl_replay",
  VSL_SEEK: "vsl_seek",
  VSL_EXIT: "vsl_exit",
  VSL_QUALITY_CHANGE: "vsl_quality_change",
  VSL_BUFFERING: "vsl_buffering",
  VSL_ERROR: "vsl_error",
  // Qualify-form funnel (§3.2)
  FORM_START: "form_start",
  FORM_STEP_COMPLETED: "form_step_completed",
  FORM_SUBMITTED: "form_submitted",
  // Calendly / conversion (§3.4)
  CALENDLY_WIDGET_OPENED: "calendly_widget_opened",
  CALENDLY_DATE_TIME_SELECTED: "calendly_date_time_selected",
  DEMO_BOOKED: "demo_booked",
  // Main-site CTA
  DEMO_CTA_OPENED: "demo_cta_opened",
  SIGN_IN_CLICKED: "sign_in_clicked",
  SIGN_UP_CLICKED: "sign_up_clicked",
  // Error boundary
  PAGE_ERROR: "page_error",
} as const;

export type AnalyticsEvent = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Captures a typed analytics event to PostHog *and* GA4. Safe to call before
 * `loadPostHog()`.
 *
 * Pass `{ transport: "sendBeacon" }` for events that immediately precede a
 * navigation away from the page (outbound CTA clicks), otherwise the in-flight
 * XHR is cancelled on unload. `options` is PostHog-only; GTM has no equivalent
 * (set `transport_type: beacon` on the GA4 tag instead).
 */
export function capture(
  event: AnalyticsEvent,
  props?: Record<string, unknown>,
  options?: CaptureOptions,
): void {
  posthogCapture(event, props, options);
  pushToDataLayer(event, props);
}

/**
 * Captures a manual `$pageview`. Used by `__root.tsx` for both the initial
 * load and subsequent SPA route resolves.
 *
 * `$current_url` must be an absolute href — PostHog's web-analytics views parse
 * the host and pathname back out of it, and a bare path yields an empty host.
 *
 * PostHog-only on purpose: GTM's container fires its own pageview tag on load,
 * so `__root.tsx` pushes `spa_pageview` for route changes *only*. Fanning out
 * here would double-count the entry pageview in GA4.
 */
export function capturePageview(path: string): void {
  posthogCapture("$pageview", { $current_url: `${window.location.origin}${path}` });
}
